import React from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

const Parchment = ReactQuill.Quill.import('parchment')
// line-height
const lineHeightConfig = {
    scope: Parchment.Scope.INLINE,
    whitelist: ['1', '1.2', '1.5', '1.6', '1.8', '2', '2.4', '2.8'],
}
const lineHeightClass = new Parchment.Attributor.Class('lineheight', 'ql-line-height', lineHeightConfig)
const lineHeightStyle = new Parchment.Attributor.Style('lineheight', 'line-height', lineHeightConfig)
Parchment.register(lineHeightClass)
Parchment.register(lineHeightStyle)
// font-size
const fontSizeConfig = {
    scope: Parchment.Scope.INLINE,
    whitelist: [
        '8px',
        '9px',
        '10px',
        '11px',
        '12px',
        '14px',
        '16px',
        '18px',
        '20px',
        '24px',
        '30px',
        '36px',
        '48px',
        '60px',
        '72px',
        '96px',
    ],
}
const fontSizeClass = new Parchment.Attributor.Class('fontsize', 'ql-font-size', fontSizeConfig)
const fontSizeStyle = new Parchment.Attributor.Style('fontsize', 'font-size', fontSizeConfig)
Parchment.register(fontSizeClass)
Parchment.register(fontSizeStyle)

const // To add a new font:
    // 1. Add font into this array
    // 2. Add classes as UpperCamelCase in rmx-text_editor.css
    fonts = [
        'Roboto',
        'Roboto Condensed',
        'Open Sans',
        'Open Sans Condensed',
        'Lato',
        'Montserrat',
        'Oswald',
        'Merriweather',
        'Ubuntu',
        'Lobster',
        'Pacifico',
        'Vollkorn',
        'Cuprum',
        'Alegreya Sans',
        'Russo One',
        'Playfair Display SC',
        'Alice',
        'Press Start 2P',
        'Bad Script',
        'Yeseva One',
        'Marmelad',
        'Rubik Mono One',
        'Raleway',
        'Roboto Slab',
        'Lora',
        'Seymour One',
        'Cormorant SC',
        'Literata',
        'Spectral',
        'Alegreya',
        'EB Garamond',
        'Bitter',
        'PT Serif',
        'Noto Sans',
    ],
    importedFonts = {
        // 'Ubuntu': true | false
        // ...
    }

// https://github.com/zenoamaro/react-quill/issues/273
const Font = ReactQuill.Quill.import('formats/font')
Font.whitelist = fonts.map(f => f.replace(/ /g, ''))
ReactQuill.Quill.register(Font, true)

const modules = {
    toolbar: [
        [{ font: Font.whitelist }, { fontsize: fontSizeConfig.whitelist }],
        ['bold', 'italic', 'underline', { color: [] }, { background: [] }],
        [{ align: [] }, { lineheight: lineHeightConfig.whitelist }],
        ['link', 'clean'],
    ],
}

export function getStylesheetLink(family) {
    return `https://fonts.googleapis.com/css?family=${family}`
}

export function addFont(family) {
    if (!importedFonts[family]) {
        const link = document.createElement('link')
        link.href = getStylesheetLink(family)
        link.rel = 'stylesheet'
        document.getElementsByTagName('head')[0].append(link)
        importedFonts[family] = true
    }
}

class Text extends React.Component {
    static getDerivedStateFromProps(props, state) {
        if (state.prevPropText !== props.text) {
            return {
                prevPropText: props.text,
                html: props.text,
            }
        }
        return {}
    }

    constructor(props) {
        super(props)
        this.state = {
            html: props.text,
            focused: false,
            prevPropText: props.text,
        }
        this.handleChange = this.handleChange.bind(this)
        this.onFocus = this.onFocus.bind(this)
        this.onBlur = this.onBlur.bind(this)
        this.onToolbarClick = this.onToolbarClick.bind(this)
        this.contentRef = React.createRef()
        this.editorRef = React.createRef()
        this.blurTimeout = null
        this.stopBlurTimeout = null
        this.toolbarListeners = [
            { e: '.ql-toolbar', on: 'click' },
            { e: '.ql-tooltip', on: 'click' },
            { e: '.ql-align', on: 'mousedown' },
        ]
    }

    componentDidMount() {
        this.observeToolbar()
        // import all fonts in edit mode
        fonts.forEach(f => addFont(f))
    }

    componentDidUpdate(prevProps, prevState) {
        const { focused } = this.state,
            prevFocused = prevState.focused

        if (!focused && prevFocused) {
            // выход из режима ввода текста - делаем сохранение в remix
            this.props.onUpdate({
                [this.props.id]: {
                    data: { [this.props.propName || 'text']: this.state.html },
                },
            })
        }
    }

    componentWillUnmount() {
        if (this.stopBlurTimeout) {
            clearTimeout(this.stopBlurTimeout)
            this.stopBlurTimeout = null
        }
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout)
            this.blurTimeout = null
        }
        this.observeToolbar(false)
    }

    onFocus() {
        const { focused } = this.state
        if (!focused) {
            this.setState({
                focused: true,
            })
            this.editorRef.current.focus()
        }
    }

    onBlur() {
        this.clearBlurTimeout()
        this.blurTimeout = setTimeout(
            () =>
                this.setState({
                    focused: false,
                }),
            200,
        )
    }

    clearBlurTimeout() {
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout)
            this.blurTimeout = null
        }
    }

    onToolbarClick(e) {
        this.stopBlurTimeout = setTimeout(() => this.clearBlurTimeout(), 100)
        e.preventDefault()
        e.stopPropagation()
    }

    handleChange(html) {
        this.setState(prevState => {
            if (html !== prevState.html) {
                return { html }
            }
            return void 0
        })
    }

    observeToolbar(add = true) {
        if (this.contentRef.current) {
            this.toolbarListeners.forEach(l => {
                if (l.target) {
                    l.target.removeEventListener(l.on, this.onToolbarClick)
                    l.target = null
                }
            })
            if (add) {
                // Найти тулбар и привязать клик
                // Для того, чтобы не сбрасывать фокус с текста, если мы идет в тулбар делать настройки
                this.toolbarListeners.forEach(l => {
                    l.target = this.contentRef.current.querySelector(l.e)
                    if (l.target) {
                        l.target.addEventListener(l.on, this.onToolbarClick)
                    }
                })
            }
        }
    }

    render() {
        const { focused, html } = this.state
        return (
            <div className={`qt${!focused ? ' __readonly' : ''}`} ref={this.contentRef} onMouseDown={this.onFocus}>
                <ReactQuill
                    onBlur={this.onBlur}
                    readOnly={!focused}
                    modules={modules}
                    value={html}
                    onChange={this.handleChange}
                    ref={this.editorRef}
                />
            </div>
        )
    }
}

export default Text
